import { DatePipe, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    input,
    OnInit,
    signal,
    ViewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { RouterModule } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DataStateChangeEvent, SelectableSettings } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { catchError, EMPTY, finalize, Observable, switchMap } from 'rxjs';
import { QualifiedCandidatesService } from 'src/app/accountmanagers/services/qualified-candidates.service';
import { MatchBreakdownService } from 'src/app/shared/components/match-breakdown/match-breakdown.service';
import { GridColumnDirective } from 'src/app/shared/grid/grid-column.directive';
import { GridComponent } from 'src/app/shared/grid/grid.component';
import { ConfirmationDialogSettings } from 'src/app/shared/models/confirmation-dialog-settings.model';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import { JobMatchingService } from 'src/app/shared/services/job-matching.service';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { SubmittalJobQualifications } from '../../candidates/submittal/models/submittal-job-qualifications.model';
import { RecruitingService } from '../../recruiting.service';
import { RecruiterQualifiedCandidate } from './models/recruiter-qualified-candidate.model';
import { MatDialog } from '@angular/material/dialog';
import {
    RecruiterQualifiedRecommendJobComponent,
    RecruiterQualifiedRecommendJobDialogData
} from './dialogs/recruiter-qualified-recommend-job.component';
import { RecommendJobEventsService } from '../recommend-job/services/recommend-job-events.service';
import { SectionCodes } from '../recommend-job/models/section-codes';
import { EventTypeCodes } from '../recommend-job/models/event-type-codes';
import { LoggerService } from 'src/app/shared/services/logger.service';

@UntilDestroy()
@Component({
    selector: 'recruiter-qualified-candidates-grid',
    templateUrl: './recruiter-qualified-candidates-grid.component.html',
    styleUrls: ['./recruiter-qualified-candidates-grid.component.scss'],
    standalone: true,
    imports: [
        NgClass,
        MatButtonModule,
        MatFormFieldModule,
        MatSelectModule,
        GridComponent,
        GridColumnDirective,
        MatProgressSpinnerModule,
        ReactiveFormsModule,
        RouterModule,
        MatIconModule,
        DatePipe
    ],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecruiterQualifiedCandidatesGridComponent implements OnInit {
    @ViewChild('grid') novaGrid: GridComponent;
    jobId = input.required<number>();
    private readonly _service = inject(RecruitingService);
    private readonly _matchBreakdownService = inject(MatchBreakdownService);
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _jobMatchingService = inject(JobMatchingService);
    private readonly _confirmDialogService = inject(ConfirmDialogService);
    private readonly _qualifiedCandidatesService: QualifiedCandidatesService = inject(QualifiedCandidatesService);
    private readonly _toasterService = inject(ToasterService);
    private readonly _featureManager = inject(LDFeatureManager);
    private readonly _dialog = inject(MatDialog);
    private readonly _datePipe = inject(DatePipe);
    private readonly _eventsService = inject(RecommendJobEventsService);
    private readonly _logger = inject(LoggerService);

    data = signal<RecruiterQualifiedCandidate[]>([]);
    originalData: RecruiterQualifiedCandidate[] = [];
    isFirstLoad = signal(true);
    isLoading = signal(true);
    totalCandidates = computed(() => this.data().length);
    hasCandidates = computed(() => this.totalCandidates() > 0);
    selectedCandidateUserIds = signal<number[]>([]);
    selectableSettings = signal<SelectableSettings>({ mode: 'multiple' });
    isProcessingCandidateRemovals = signal(false);
    sort: SortDescriptor[] = [];
    isEnableSorting = signal(false);

    enabledRecommendedJobs = toSignal(
        this._featureManager.isEnabled(FeatureFlag.RecommendJobsToCandidatesOnRecruitersJobDetailsPage)
    );

    enabledAdditionalColumns = toSignal(
        this._featureManager.isEnabled(FeatureFlag.EnableRecruiterQualifiedCandidatesAdditionalColumns)
    );

    isRecommendedJobsEventLoggingEnabled = toSignal(
        this._featureManager.isEnabled(FeatureFlag.RecommendedJobsEventLogging),
        { initialValue: false }
    );

    ngOnInit(): void {
        this._featureManager
            .isEnabled(FeatureFlag.EnableSortQualifiedCandidatesOnRecruitersJobDetailsPage)
            .pipe(untilDestroyed(this))
            .subscribe((isEnabled) => {
                this.isEnableSorting.set(isEnabled);
            });
        this.loadGridData();
    }

    loadGridData(): void {
        this.isLoading.set(true);
        this._service
            .getRecruiterQualifiedCandidates(this.jobId())
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (candidates) => {
                    if (this.enabledAdditionalColumns()) {
                        this.formatLicenses(candidates);
                        this.formatExpertises(candidates);
                    }
                    this.originalData = candidates;
                    // Update signals
                    this.data.set(candidates);
                    this.isLoading.set(false);
                    if (this.isFirstLoad()) {
                        this.isFirstLoad.set(false);
                    }
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    console.error('Error fetching RecruiterQualifiedCandidates:', err);
                    this.data.set([]);
                    this.isLoading.set(false);
                    if (this.isFirstLoad()) {
                        this.isFirstLoad.set(false);
                    }
                    this._cdr.markForCheck();
                }
            });
    }

    openBreakdownDialog(data: RecruiterQualifiedCandidate) {
        const qualifications$: Observable<SubmittalJobQualifications> = this._jobMatchingService.getQualifications(
            data.candidateUserId,
            [this.jobId()]
        );
        qualifications$.pipe(untilDestroyed(this)).subscribe({
            next: (qualifications: SubmittalJobQualifications) => {
                this._matchBreakdownService.openMatchBreakdownDialogForJob(
                    qualifications,
                    data.candidateUserId,
                    this.jobId(),
                    data.candidateFullName,
                    false
                );
            },
            error: (error) => {
                console.error('Error fetching qualifications:', error);
            },
            complete: () => {
                console.log('Completed fetching qualifications');
            }
        });
    }

    onRowSelected(selectedRows: RecruiterQualifiedCandidate[]): void {
        this.selectedCandidateUserIds.set(selectedRows?.map((r) => r.candidateUserId) ?? []);
    }

    onClickRemoveCandidates(): void {
        const settings: ConfirmationDialogSettings = {
            dataQaId: 'QualifiedCandidatesRemoveCandidateDialog',
            title: 'Remove Candidate',
            message: `Are you sure you want to remove ${this.selectedCandidateUserIds().length} candidate${this.selectedCandidateUserIds().length > 1 ? 's' : ''} from the list? This action cannot be undone.`,
            action: 'YES, REMOVE',
            cancelText: 'CANCEL',
            needCross: true
        };
        this._confirmDialogService
            .showDialog(settings)
            .pipe(
                switchMap((confirm) => {
                    if (!confirm) {
                        return EMPTY;
                    }
                    this.isProcessingCandidateRemovals.set(true);
                    return this._qualifiedCandidatesService.saveQualifiedCandidateRemovals({
                        jobId: this.jobId(),
                        candidateUserIds: this.selectedCandidateUserIds()
                    });
                }),
                finalize(() => {
                    this.isProcessingCandidateRemovals.set(false);
                }),
                untilDestroyed(this)
            )
            .subscribe({
                next: () => {
                    this.novaGrid.clearRowSelection();
                    this.loadGridData();
                },
                error: () => {
                    this._toasterService.toast('An error occurred while removing Candidates.');
                }
            });
    }

    onRecommendJobClick(): void {
        const dialogRef = this._dialog.open(RecruiterQualifiedRecommendJobComponent, {
            panelClass: ['nova__dialog'],
            disableClose: false,
            maxWidth: 500,
            data: {
                userIds: this.selectedCandidateUserIds,
                jobId: this.jobId,
                candidateNames: signal(
                    this.selectedCandidateUserIds().map((id) => {
                        const candidate = this.data().find((c) => c.candidateUserId === id);
                        return candidate ? candidate.candidateFullName : '';
                    })
                )
            } satisfies RecruiterQualifiedRecommendJobDialogData
        });

        dialogRef
            .afterClosed()
            .pipe(
                untilDestroyed(this),
                switchMap((result) => {
                    if (result?.success) {
                        const userIds = [...this.selectedCandidateUserIds()];
                        const jobIds = [this.jobId()];
                        this.novaGrid.clearRowSelection();
                        this.loadGridData();
                        this._toasterService.toast('Job recommendation(s) sent');
                        if (this.isRecommendedJobsEventLoggingEnabled()) {
                            return this._eventsService
                                .saveEvent({
                                    sectionCode: SectionCodes.RecruitingQualifiedCandidatesTab,
                                    eventTypeCode: EventTypeCodes.RecommendJob,
                                    candidateUserIds: userIds,
                                    jobIds: jobIds
                                })
                                .pipe(
                                    catchError((error) => {
                                        this._logger.error('Error logging recommended job event:', error);
                                        return EMPTY;
                                    })
                                );
                        }
                        return EMPTY;
                    }
                    return EMPTY;
                })
            )
            .subscribe();
    }

    onDataStateChange(state: DataStateChangeEvent): void {
        this.sort = state.sort ?? [];

        const sorted = this.applySort(this.originalData, this.sort);

        this.data.set(sorted);
        this._cdr.markForCheck();
    }

    hasAnyValue(field: keyof RecruiterQualifiedCandidate): boolean {
        return this.data().some((item) => item[field] != null);
    }

    private applySort(data: RecruiterQualifiedCandidate[], sort: SortDescriptor[]): RecruiterQualifiedCandidate[] {
        if (!sort || sort.length === 0) {
            return data;
        }

        const { field, dir } = sort[0];
        const direction = dir === 'asc' ? 1 : -1;

        const dateFields = ['availableDate', 'recommendedDate', 'interestedDate', 'lastContacted'];
        const getString = (value?: string) => (value || '').toLowerCase();

        // Compare candidate lastname and then firstname
        const compareCandidateName = (a: RecruiterQualifiedCandidate, b: RecruiterQualifiedCandidate) => {
            const cmpLast = getString(a.candidateLastName).localeCompare(getString(b.candidateLastName));
            if (cmpLast !== 0) {
                return cmpLast * direction;
            }

            const cmpFirst = getString(a.candidateFirstName).localeCompare(getString(b.candidateFirstName));
            return cmpFirst * direction;
        };

        // Generic value comparison with candidate name tie-breaker
        const compareValues = (aValue: any, bValue: any, tieBreaker?: (a: any, b: any) => number) => {
            if (aValue == null && bValue == null) {
                return tieBreaker ? tieBreaker(aValue, bValue) : 0;
            }
            if (aValue == null) {
                return 1;
            }
            if (bValue == null) {
                return -1;
            }
            if (aValue < bValue) {
                return -1 * direction;
            }
            if (aValue > bValue) {
                return 1 * direction;
            }
            return tieBreaker ? tieBreaker(aValue, bValue) : 0;
        };

        const matchOrder: Record<string, number> = { High: 2, Medium: 1, Low: 0 };
        const unknownOrder = 99; // Handles edge case for null value

        // Main sort logic
        return [...data].sort((a, b) => {
            // Candidate column: sort by lastname then by firstname then by availableDate
            if (field === 'candidate') {
                const cmpName = compareCandidateName(a, b);
                if (cmpName !== 0) {
                    return cmpName;
                }

                const dateA = a.availableDate ? new Date(a.availableDate).getTime() : null;
                const dateB = b.availableDate ? new Date(b.availableDate).getTime() : null;
                return compareValues(dateA, dateB);
            } else if (field === 'matchCategory') {
                // MatchCategory column
                const aOrder = matchOrder[a.matchCategory] ?? unknownOrder;
                const bOrder = matchOrder[b.matchCategory] ?? unknownOrder;
                return compareValues(aOrder, bOrder, () => compareCandidateName(a, b));
            } else if (dateFields.includes(field)) {
                // Date columns: sort by date (nulls last) then by candidate lastname then by candidate firstname
                const dateA = a[field] ? new Date(a[field]).getTime() : null;
                const dateB = b[field] ? new Date(b[field]).getTime() : null;
                return compareValues(dateA, dateB, () => compareCandidateName(a, b));
            }

            // Other columns: nulls last, sort by candidate lastname then by candidate firstname
            return compareValues(a[field], b[field], () => compareCandidateName(a, b));
        });
    }

    private formatLicenses(candidates: RecruiterQualifiedCandidate[]): void {
        candidates.forEach((candidate) => {
            candidate.formattedLicenses = candidate.licenses.map((license) => {
                const expiration = license.expirationDate
                    ? (() => {
                          const formattedExpiration =
                              this._datePipe.transform(license.expirationDate, 'MM/dd/yyyy') ?? 'Missing';
                          return ` (Exp: ${formattedExpiration})`;
                      })()
                    : ' (Exp: Missing)';
                return `${license.stateCode}${expiration}`;
            });
        });
    }

    private formatExpertises(candidates: RecruiterQualifiedCandidate[]): void {
        candidates.forEach((candidate) => {
            candidate.formattedExpertises = candidate.expertises.map(
                (expertise) => `${expertise.abbreviation} (${expertise.yearsOfExperience})`
            );
        });
    }
}
